import FileBrowser, {
  FileData,
  UseLoadFile,
  UseLoadFileUrl,
} from '@wandb/weave/common/components/FileBrowser';
import React, {useCallback, useEffect} from 'react';
import {useLocation, useParams} from 'react-router-dom';

import {LoaderProxy} from '../containers/AppLoader';
import {useRunFilesQuery} from '../generated/graphql';
import {
  FileDownloadLocation,
  trackFileDownload,
} from '../services/analytics/fileDownloadEvents';
import {File} from '../state/graphql/runFilesQuery';
import {useSingleFileQuery} from '../state/graphql/singleFileQuery';
import {MAX_NUM_FILES} from '../util/constants';
import {trackPageViewed} from '../util/navigation';
import {useLoadFile} from '../util/requests';
import * as Urls from '../util/urls';

// Handles multipart files and optional custom labels.
const CONSOLE_LOGS_FILENAME_REGEX = /^(logs\/)?output(.+)?\.log$/;

interface RunFilesProps {
  isCodeHidden?: boolean;
  entityName: string;
  projectName: string;
  runName: string;
  path?: string;
  history: any;
  groupName?: string;
  sweepName?: string;
  setSelectedRunName?: (name: string) => void;
}

const RunFiles = (props: RunFilesProps) => {
  // for page view tracking
  useEffect(() => {
    let pageName = 'Run';
    if (props.groupName != null) {
      pageName = 'Group run';
    } else if (props.sweepName != null) {
      pageName = 'Sweep run';
    }
    trackPageViewed(pageName, 'Files');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    entityName,
    projectName,
    runName,
    history,
    path,
    groupName,
    setSelectedRunName = () => null,
  } = props;

  const location = useLocation();
  const urlParams = useParams<{
    groupName?: string;
    tab?: string;
    runTab?: string;
  }>();
  const isGroupPage = urlParams.groupName != null;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlRunName = params.get('runName');
    if (urlRunName) {
      setSelectedRunName(urlRunName);
    }
  }, [setSelectedRunName, location.search]);

  const setFilePath = useCallback(
    (newPathArray: string[]) => {
      history.push(
        groupName && isGroupPage
          ? Urls.runGroup({
              entityName,
              projectName,
              name: groupName,
              tab: urlParams.tab,
              runTab: urlParams.runTab,
              filePath: newPathArray,
              runName,
            })
          : Urls.runTabFile(entityName, projectName, runName, newPathArray)
      );
    },
    [
      entityName,
      projectName,
      runName,
      history,
      groupName,
      isGroupPage,
      urlParams.tab,
      urlParams.runTab,
    ]
  );

  const handleDownload = useCallback(
    (file: FileData) => {
      const location: FileDownloadLocation = props.sweepName
        ? 'sweep-files'
        : groupName
          ? 'group-files'
          : 'run-files';

      if (CONSOLE_LOGS_FILENAME_REGEX.test(file.name)) {
        trackFileDownload({
          fileType: 'log',
          isMultipartLogsFile: file.name.startsWith('logs/'),
          location,
        });
      }
    },
    [groupName, props.sweepName]
  );

  const runFilesQuery = useRunFilesQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      entityName,
      projectName,
      runName,
      maxNumFiles: MAX_NUM_FILES,
    },
  });

  if (runFilesQuery.loading) {
    return <LoaderProxy name="run-files-loader" />;
  }
  const files =
    runFilesQuery.data?.project?.run?.files?.edges.map(e => e.node as File) ??
    [];

  const useLoadRunFile: UseLoadFile = (file, options) => {
    return useLoadFile({entityName, projectName, runName}, file.name, options);
  };
  const useLoadRunFileUrl: UseLoadFileUrl = file => {
    return useSingleFileQuery({
      entityName,
      projectName,
      runName,
      fileName: file.name,
    });
  };

  return (
    <FileBrowser
      totalFiles={runFilesQuery.data?.project?.run?.fileCount ?? 0}
      isCodeHidden={props.isCodeHidden}
      files={files}
      path={path}
      history={history}
      useLoadFile={useLoadRunFile}
      useLoadFileUrl={useLoadRunFileUrl}
      setFilePath={setFilePath}
      onDownload={handleDownload}
    />
  );
};

export default RunFiles;
